//TODO : Refactor after ai
/**
 * Modul 4 – Baustellenaufnahme & Kalkulation (Tablet).
 * Strasse aufnehmen → Stationierung → Schadstellen → rechts live Material, Team, Dauer, Richtpreis.
 * Kalkulationsregeln: ANNAHMEN C4, Stationierung: ANNAHMEN C5.
 */
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDatenStore } from "@app/store";
import {
  angebotAusAufnahme,
  datensatz,
  datensaetze,
  heute,
  kalkulationAusAufnahme,
  rolle as rolleVon,
} from "@shared/demo-data/viacid";
import {
  datumCH,
  fr,
  mit,
  num,
  stationMeter,
  str,
  zahl,
  ziel,
  neueId,
} from "./lib";
import {
  Beispiel,
  Feld,
  Hinweis,
  Karte,
  Kennzahl,
  Knopf,
  Pille,
  Seite,
  Zeile,
} from "./ui";
import { ActionLeiste } from "./ActionLeiste";
import { objektKlasse as klasseVon } from "@shared/demo-data/viacid";

export function AufnahmeListePage() {
  const b = useDatenStore((s) => s.bestand);
  const liste = datensaetze(b, "baustellenaufnahme");
  return (
    <Seite
      titel="Aufnahmen"
      untertitel="Strassenbegehungen mit Vorausmass – mobil erfassen, im Büro kalkulieren"
    >
      <div className="vc-card vc-list">
        {liste.map((a) => (
          <Zeile
            key={a.id}
            titel={a.titel}
            meta={`${datumCH(a.werte.aufnahmedatum)} · ${str(a.werte.abschnitt)} · ${ziel(b, a, "baustellenaufnahme-betrifft-strassenobjekt", "strassenobjekt")?.titel ?? ""}`}
            rechts={<Pille status={a.status} />}
            zu={`/app/aufnahme/${a.id}`}
          />
        ))}
      </div>
      <p className="sub" style={{ marginTop: 12 }}>
        Neue Aufnahmen entstehen aus einer Verkaufschance („Aufnahme starten“).
      </p>
    </Seite>
  );
}

const SCHADENSARTEN = [
  "Längsriss",
  "Querriss",
  "Netzrisse",
  "Schlagloch",
  "Randsteinfuge offen",
  "Belag ausgemagert",
  "Schachtrahmen lose",
];
const EINHEITEN = ["lfm", "m²", "Stk"];

export function AufnahmePage() {
  const { id = "" } = useParams();
  const b = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const aendern = useDatenStore((s) => s.aendern);
  const anlegen = useDatenStore((s) => s.anlegen);
  const anwenden = useDatenStore((s) => s.anwenden);
  const nav = useNavigate();
  const [aktiv, setAktiv] = useState<string | null>(null);
  const [neu, setNeu] = useState({
    von: "",
    bis: "",
    schadensart: SCHADENSARTEN[0],
    menge: "",
    einheit: "lfm",
    schweregrad: "mittel",
    massnahme: "",
  });

  const a = datensatz(b, "baustellenaufnahme", id);
  const massnahmen = datensaetze(b, "massnahmenart");
  const stellen = useMemo(
    () =>
      a
        ? mit(
            b,
            "schadstelle",
            "schadstelle-gehoert-zu-baustellenaufnahme",
            a.id
          )
        : [],
    [b, a]
  );
  const kalkulationen = a
    ? mit(b, "kalkulation", "kalkulation-basiert-auf-baustellenaufnahme", a.id)
    : [];
  const angebote = a
    ? datensaetze(b, "angebot").filter((x) =>
        kalkulationen.some((k) =>
          [x.bezuege["angebot-basiert-auf-kalkulation"]].flat().includes(k.id)
        )
      )
    : [];

  if (!a) return <Hinweis ton="red">Aufnahme nicht gefunden.</Hinweis>;
  const strasse = ziel(
    b,
    a,
    "baustellenaufnahme-betrifft-strassenobjekt",
    "strassenobjekt"
  );
  const chance = ziel(
    b,
    a,
    "baustellenaufnahme-fuer-verkaufschance",
    "verkaufschance"
  );
  const laenge = num(a.werte.gemesseneLaenge);
  const breite = num(a.werte.gemesseneBreite);
  const skala = Math.max(
    laenge,
    50,
    ...stellen.map((s) => stationMeter(s.werte.position)?.bis ?? 0)
  );
  const abgeschlossen = a.status !== "In Aufnahme";

  // Live-Kalkulation je Massnahmenart (ANNAHMEN C4)
  const kalk = massnahmen
    .map((m) => {
      const eigene = stellen.filter(
        (s) => s.bezuege["schadstelle-erfordert-massnahmenart"] === m.id
      );
      const menge = eigene.reduce((s, x) => s + num(x.werte.schadensmenge), 0);
      if (!menge) return null;
      const material =
        menge * num(m.werte.bindemittelansatz) +
        menge * num(m.werte.splittansatz);
      const stunden = num(m.werte.leistungJeStunde)
        ? menge / num(m.werte.leistungJeStunde)
        : 0;
      const team = num(m.werte.teamgroesse) || 3;
      const halbtage = Math.max(1, Math.ceil(stunden / 4));
      return {
        m,
        menge,
        einheit: str(m.werte.abrechnungseinheit),
        material,
        stunden,
        team,
        halbtage,
        preis: menge * num(m.werte.einheitspreis),
      };
    })
    .filter((x): x is NonNullable<typeof x> => Boolean(x));
  const richtpreis = kalk.reduce((s, k) => s + k.preis, 0);

  const setWert = (key: string, wert: number | string) => {
    const werte: Record<string, number | string> = { [key]: wert };
    const l = key === "gemesseneLaenge" ? Number(wert) : laenge;
    const br = key === "gemesseneBreite" ? Number(wert) : breite;
    werte.vorausmass = Math.round(l * br);
    aendern({ slug: "baustellenaufnahme", id: a.id, werte });
  };

  const schadstelleHinzufuegen = () => {
    const menge = Number(neu.menge);
    if (!menge) return;
    const von = Number(neu.von) || 0;
    const bis = Number(neu.bis) || von;
    const pos = `Station ${Math.floor(von / 1000)}+${String(von % 1000).padStart(3, "0")} bis ${Math.floor(bis / 1000)}+${String(bis % 1000).padStart(3, "0")}`;
    const m =
      massnahmen.find((x) => x.id === neu.massnahme) ??
      massnahmen.find((x) => str(x.werte.abrechnungseinheit) === neu.einheit);
    const nid = neueId("s");
    anlegen(
      "schadstelle",
      {
        id: nid,
        titel: `${neu.schadensart} · ${pos}`,
        untertitel: `${menge} ${neu.einheit} · Schweregrad ${neu.schweregrad}`,
        kennung: null,
        status: null,
        werte: {
          position: pos,
          schadensart: neu.schadensart,
          schadensmenge: menge,
          mengeneinheit: neu.einheit,
          schweregrad: neu.schweregrad,
          geschaetzteMaterialmenge: m
            ? Math.round(menge * num(m.werte.bindemittelansatz) * 10) / 10
            : 0,
        },
        bezuege: {
          "schadstelle-gehoert-zu-baustellenaufnahme": a.id,
          ...(m ? { "schadstelle-erfordert-massnahmenart": m.id } : {}),
        },
      },
      "Schadstelle erfasst."
    );
    setNeu({ ...neu, von: String(bis), bis: "", menge: "" });
    setAktiv(nid);
  };

  const abschliessen = () =>
    aendern(
      { slug: "baustellenaufnahme", id: a.id, status: "Aufgenommen" },
      "Aufnahme abgeschlossen."
    );

  const angebotErstellen = () => {
    const persona = rolleVon(rolle)?.persona ?? null;
    const k = kalkulationAusAufnahme(b, a.id);
    anwenden(k);
    if (k.fehler) return;
    const zwischen = useDatenStore.getState();
    for (const n of k.neu.filter((x) => x.slug === "kalkulation"))
      zwischen.aendern({
        slug: "kalkulation",
        id: n.satz.id,
        status: "Freigegeben",
        werte: { freigegebenAm: heute() },
      });
    const b2 = useDatenStore.getState().bestand;
    const an = angebotAusAufnahme(b2, a.id, persona);
    anwenden(an);
    const neuAn = an.neu.find((x) => x.slug === "angebot");
    if (neuAn) nav(`/app/angebote/${neuAn.satz.id}`);
  };

  return (
    <Seite
      titel={a.titel}
      untertitel={
        <>
          {strasse?.titel} · {str(strasse?.werte.adresse)} ·{" "}
          {datumCH(a.werte.aufnahmedatum)} <Pille status={a.status} />
        </>
      }
      aktionen={
        <>
          {chance && (
            <Knopf onClick={() => nav(`/app/chancen/${chance.id}`)}>
              Verkaufschance
            </Knopf>
          )}
          {!abgeschlossen && (
            <Knopf onClick={abschliessen} disabled={stellen.length === 0}>
              Aufnahme abschliessen
            </Knopf>
          )}
          {angebote.length === 0 ? (
            <Knopf
              art="primary"
              onClick={angebotErstellen}
              disabled={stellen.length === 0}
            >
              Angebot erstellen
            </Knopf>
          ) : (
            <Knopf
              art="primary"
              onClick={() => nav(`/app/angebote/${angebote[0].id}`)}
            >
              Angebot {angebote[0].kennung}
            </Knopf>
          )}
          {klasseVon("baustellenaufnahme") && (
            <ActionLeiste
              klasse={klasseVon("baustellenaufnahme")!}
              satz={a}
              ausser={[
                "Aufnahme abschließen",
                "angebot-erstellen",
                "Schadstelle erfassen",
                "Baustellenfoto hinzufügen",
              ]}
            />
          )}
        </>
      }
    >
      <div className="vc-grid main-side">
        <div className="vc-grid">
          <Karte
            titel="Strasse"
            sub="Masse direkt auf dem Tablet erfassen – Vorausmass rechnet mit"
          >
            <div className="vc-grid c3">
              <Feld label="Länge (m)">
                <input
                  type="number"
                  inputMode="decimal"
                  value={laenge || ""}
                  disabled={abgeschlossen}
                  onChange={(e) =>
                    setWert("gemesseneLaenge", Number(e.target.value))
                  }
                />
              </Feld>
              <Feld label="Breite (m)">
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={breite || ""}
                  disabled={abgeschlossen}
                  onChange={(e) =>
                    setWert("gemesseneBreite", Number(e.target.value))
                  }
                />
              </Feld>
              <Feld label="Abschnitt">
                <input
                  value={str(a.werte.abschnitt)}
                  disabled={abgeschlossen}
                  onChange={(e) =>
                    aendern({
                      slug: "baustellenaufnahme",
                      id: a.id,
                      werte: { abschnitt: e.target.value },
                    })
                  }
                />
              </Feld>
            </div>
            <div className="vc-grid c3" style={{ marginTop: 12 }}>
              <Kennzahl
                label="Vorausmass"
                wert={`${zahl(laenge * breite)} m²`}
              />
              <Kennzahl label="Schadstellen" wert={stellen.length} />
              <Kennzahl
                label="Fotos"
                wert={
                  mit(
                    b,
                    "baustellenfoto",
                    "baustellenfoto-gehoert-zu-baustellenaufnahme",
                    a.id
                  ).length
                }
                hint="Vorher-Fotos"
              />
            </div>
          </Karte>

          <Karte
            titel="Stationierung"
            sub={`0 m bis ${zahl(skala)} m · Schadstellen antippen`}
          >
            <div className="vc-station">
              <div className="line" />
              {stellen.map((s) => {
                const m = stationMeter(s.werte.position);
                if (!m) return null;
                const l = (100 * m.von) / skala;
                const w = Math.max(1, (100 * (m.bis - m.von)) / skala);
                return (
                  <div
                    key={s.id}
                    className={`mark ${aktiv === s.id ? "active" : ""} ${str(s.werte.schweregrad) === "hoch" ? "hoch" : ""}`}
                    style={{ left: `${l}%`, width: `${w}%` }}
                    title={s.titel}
                    onClick={() => setAktiv(s.id)}
                  />
                );
              })}
              {[0, 0.25, 0.5, 0.75, 1].map((t) => (
                <span key={t} className="tick" style={{ left: `${t * 100}%` }}>
                  {zahl(t * skala)} m
                </span>
              ))}
            </div>
            <div className="vc-list" style={{ marginTop: 8 }}>
              {stellen.map((s) => {
                const m = ziel(
                  b,
                  s,
                  "schadstelle-erfordert-massnahmenart",
                  "massnahmenart"
                );
                return (
                  <Zeile
                    key={s.id}
                    titel={s.titel}
                    meta={`${num(s.werte.schadensmenge)} ${str(s.werte.mengeneinheit)} · ${str(s.werte.beschreibung) || "—"}`}
                    rechts={
                      <Pille farbe={m ? "orange" : "red"}>
                        {m?.titel ?? "Massnahme fehlt"}
                      </Pille>
                    }
                    onClick={() => setAktiv(s.id)}
                  />
                );
              })}
            </div>
          </Karte>

          {!abgeschlossen && (
            <Karte
              titel="Schadstelle erfassen"
              sub="Position, Schaden, Menge – Massnahme wird vorgeschlagen"
            >
              <div className="vc-grid c3">
                <Feld label="Von (m)">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={neu.von}
                    onChange={(e) => setNeu({ ...neu, von: e.target.value })}
                  />
                </Feld>
                <Feld label="Bis (m)">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={neu.bis}
                    onChange={(e) => setNeu({ ...neu, bis: e.target.value })}
                  />
                </Feld>
                <Feld label="Schadensart">
                  <select
                    value={neu.schadensart}
                    onChange={(e) =>
                      setNeu({ ...neu, schadensart: e.target.value })
                    }
                  >
                    {SCHADENSARTEN.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Feld>
                <Feld label="Menge">
                  <input
                    type="number"
                    inputMode="decimal"
                    value={neu.menge}
                    onChange={(e) => setNeu({ ...neu, menge: e.target.value })}
                  />
                </Feld>
                <Feld label="Einheit">
                  <select
                    value={neu.einheit}
                    onChange={(e) =>
                      setNeu({ ...neu, einheit: e.target.value })
                    }
                  >
                    {EINHEITEN.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Feld>
                <Feld label="Schweregrad">
                  <select
                    value={neu.schweregrad}
                    onChange={(e) =>
                      setNeu({ ...neu, schweregrad: e.target.value })
                    }
                  >
                    {["gering", "mittel", "hoch"].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Feld>
                <Feld label="Massnahme (Vorschlag nach Einheit)">
                  <select
                    value={neu.massnahme}
                    onChange={(e) =>
                      setNeu({ ...neu, massnahme: e.target.value })
                    }
                  >
                    <option value="">automatisch</option>
                    {massnahmen.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.titel}
                      </option>
                    ))}
                  </select>
                </Feld>
                <Feld label="Foto">
                  <button
                    className="vc-btn"
                    type="button"
                    onClick={() =>
                      anlegen(
                        "baustellenfoto",
                        {
                          id: neueId("bf"),
                          titel: "Foto vorher",
                          untertitel: heute(),
                          kennung: null,
                          status: null,
                          werte: {
                            fotoart: "Vorher",
                            aufnahmezeitpunkt: heute(),
                            beschreibung: neu.schadensart,
                          },
                          bezuege: {
                            "baustellenfoto-gehoert-zu-baustellenaufnahme":
                              a.id,
                            ...(aktiv
                              ? { "baustellenfoto-zeigt-schadstelle": aktiv }
                              : {}),
                          },
                        },
                        "Foto (Platzhalter) hinzugefügt."
                      )
                    }
                  >
                    📷 Foto aufnehmen
                  </button>
                </Feld>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <Knopf
                    art="primary"
                    onClick={schadstelleHinzufuegen}
                    disabled={!neu.menge}
                  >
                    Schadstelle speichern
                  </Knopf>
                </div>
              </div>
            </Karte>
          )}
        </div>

        <div className="vc-grid">
          <Karte
            titel="Kalkulation"
            sub="rechnet live aus den Schadstellen"
            rechts={<Beispiel />}
          >
            {kalk.length === 0 && (
              <div className="sub">
                Schadstellen mit Massnahme erfassen – dann rechnet es hier.
              </div>
            )}
            {kalk.map((k) => (
              <div
                key={k.m.id}
                style={{
                  borderTop: "1px solid var(--vc-line)",
                  padding: "10px 0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: 500,
                  }}
                >
                  <span>{k.m.titel}</span>
                  <span>{fr(k.preis)}</span>
                </div>
                <div className="sub">
                  {zahl(k.menge)} {k.einheit} ×{" "}
                  {fr(num(k.m.werte.einheitspreis))}
                </div>
                <div
                  className="vc-grid c3"
                  style={{ marginTop: 6, fontSize: "0.8rem" }}
                >
                  <div>
                    <div className="sub">Material</div>
                    {zahl(k.material)} kg
                  </div>
                  <div>
                    <div className="sub">Team × Stunden</div>
                    {k.team} × {zahl(k.stunden)} h
                  </div>
                  <div>
                    <div className="sub">Einsätze</div>
                    {k.halbtage} Halbtag{k.halbtage > 1 ? "e" : ""}
                  </div>
                </div>
              </div>
            ))}
            <div
              style={{
                borderTop: "2px solid var(--vc-ink)",
                marginTop: 8,
                paddingTop: 10,
                display: "flex",
                justifyContent: "space-between",
                fontWeight: 600,
                fontSize: "1.05rem",
              }}
            >
              <span>Richtpreis netto</span>
              <span>{fr(richtpreis)}</span>
            </div>
            <div className="sub" style={{ marginTop: 6 }}>
              KI/Regel entwirft, der Mitarbeiter entscheidet: Parameter je
              Massnahme unter Daten → Massnahmenarten.
            </div>
          </Karte>
          {kalkulationen.length > 0 && (
            <Karte titel="Gespeicherte Kalkulationen">
              <div className="vc-list">
                {kalkulationen.map((k) => (
                  <Zeile
                    key={k.id}
                    titel={k.titel}
                    meta={`Version ${str(k.werte.version)} · ${fr(num(k.werte.kalkulierterPreis))}`}
                    rechts={<Pille status={k.status} />}
                    zu={`/app/daten/kalkulation/${k.id}`}
                  />
                ))}
              </div>
            </Karte>
          )}
        </div>
      </div>
    </Seite>
  );
}
